import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';

import { useNavigation } from '@react-navigation/native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';

import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ds } from '@/components/lib/design.system';
import { useDeposit } from '@/feature/deposit/hooks/use-deposit';
import { handleTokenizePayment, useInitializeVenmoContext } from '@/hooks/payments/use-initialize-venmo-context';
import { useAuthUserInfo } from '@/hooks/use-auth-user-info';
import { usePreventAndroidBackNavigation } from '@/hooks/use-prevent-android-back-navigation';
import { useRootNavigation } from '@/hooks/use-root-navigation';
import { VenmoDepositNavigationProp } from '@/navigation/deposit/types';
import { DepositStackParamList } from '@/navigation/types';
import { common } from '@/styles/styles';
import { getWalletErrorCode } from '@/utils/get-wallet-error-code';

import { PaymentMethodTypes } from '../const';
import { useDepositPaymentMethods, venmoPaymentSelector } from '../hooks/use-deposit-payment-methods';
import { useDepositStatusStore } from '../hooks/use-deposit-status-store';

const styles = StyleSheet.create({
    loaderContainer: { backgroundColor: ds.colorValue.background.neutral.base },
});

type ScreenProps = NativeStackScreenProps<DepositStackParamList, 'VenmoDepositScreen'>;

export const VenmoDepositScreen = ({ route }: ScreenProps) => {
    const { currency = 'USD' } = route.params || {};
    const selectedAmount = useDepositStatusStore(state => state.selectedAmount);
    const selectedPaymentMethodType = PaymentMethodTypes.PaysafeVenmo;
    const navigation = useNavigation<VenmoDepositNavigationProp>();
    const rootNavigation = useRootNavigation();

    usePreventAndroidBackNavigation();

    const { data: userInfo } = useAuthUserInfo();
    const { data: venmoMethod } = useDepositPaymentMethods({
        select: venmoPaymentSelector,
    });
    const { t } = useTranslation(['wallet']);

    const initializeVenmo = useInitializeVenmoContext();

    // This is to prevent redundant initialization attempts, maintain the correct state of the application,
    // optimize performance by avoiding unnecessary operations, and handle errors effectively.
    const isVenmoInitialized = useRef(false);
    const paysafeAccountId = venmoMethod?.accountId;

    const { mutate: deposit } = useDeposit();

    useEffect(() => {
        const handleVenmoDeposit = async () => {
            if (userInfo && !isVenmoInitialized.current && paysafeAccountId) {
                try {
                    await initializeVenmo();
                    const token = await handleTokenizePayment(
                        parseFloat(selectedAmount),
                        paysafeAccountId,
                        userInfo,
                        navigation.navigate
                    );

                    isVenmoInitialized.current = true;

                    // Call deposit mutation after successful Venmo transaction
                    deposit(
                        {
                            type: PaymentMethodTypes.PaysafeVenmo,
                            currency,
                            payment_token: token,
                        },
                        {
                            onSuccess: () => {
                                navigation.navigate('SuccessfulDepositModal', {
                                    paymentProvider: selectedPaymentMethodType,
                                });
                            },
                            onError: error => {
                                navigation.navigate('FailedDepositModal', {
                                    paymentProvider: selectedPaymentMethodType,
                                    errorCode: getWalletErrorCode(error),
                                });
                            },
                        }
                    );
                } catch (error) {
                    // from the root, so the error modal closes and we go back to the deposit screen in the deposit stack
                    const backToDeposit = () => {
                        rootNavigation.navigate(
                            'DepositStack',
                            { screen: 'DepositScreen', params: {}, pop: true, merge: true },
                            { pop: true }
                        );
                    };
                    navigation.navigate('ErrorModal', {
                        title: t('error_processing_payment'),
                        subtitle: t('venmo_deposit_error_description'),
                        primaryButton: 'Close',
                        handlePress: backToDeposit,
                        handleSecondaryButtonPress: backToDeposit,
                        closeIconHandlePress: backToDeposit,
                    });
                }
            }
        };

        handleVenmoDeposit();
    }, [
        initializeVenmo,
        navigation,
        rootNavigation,
        selectedAmount,
        userInfo,
        deposit,
        currency,
        selectedPaymentMethodType,
        paysafeAccountId,
        t,
    ]);

    return (
        <View style={[common.flex, styles.loaderContainer, common.justifyCenter]}>
            <LoadingSpinner />
        </View>
    );
};
